import { useMemo, useReducer, useEffect } from 'react';
import { EventMode, EventPost, EventMetadata } from '@feature/event-detail/types';
import { useGetPostMetadataQuery } from '@/api/client';

interface UseEventDetailParams {
  eventId: string;
  event: EventPost;
}

interface UseEventDetailResult {
  event: EventPost;
  mode: EventMode;
  metadata: EventMetadata | null;
  isLoading: boolean;
}

export function useEventDetail({
  eventId,
  event,
}: UseEventDetailParams): UseEventDetailResult {
  const { data, isLoading } = useGetPostMetadataQuery(eventId);

  const metadata = data?.[eventId] ?? null;

  const [, forceUpdate] = useReducer((x: number) => x + 1, 0);

  const mode: EventMode = useMemo(() => {
    const now = Date.now();
    const startsAt = new Date(event.starts_at).getTime();
    const endsAt = new Date(event.ends_at).getTime();

    if (now > endsAt) return 'after';
    if (now >= startsAt) return 'during';
    return 'before';
  }, [event]);

  useEffect(() => {
    const now = Date.now();
    const startsAt = new Date(event.starts_at).getTime();
    const endsAt = new Date(event.ends_at).getTime();

    const transitions = [startsAt, endsAt].filter(t => t > now);
    if (transitions.length === 0) return;

    const nextTransition = Math.min(...transitions);
    const delay = nextTransition - now + 100;

    const timer = setTimeout(forceUpdate, delay);
    return () => clearTimeout(timer);
  }, [event, mode]);

  return {
    event,
    mode,
    metadata: metadata as EventMetadata,
    isLoading,
  };
}
